import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateContentSubmission } from '../../hooks/useContentSubmissions';
import type { ContentStatus, ContentSubmission, ContentPlatform, ContentType } from '../../types/content.types';
import { editPostFormSchema, type EditPostFormValues } from '../../validators/content.validators';

export interface EditPostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: ContentSubmission;
}

export const EditPostModal: React.FC<EditPostModalProps> = ({
  open,
  onOpenChange,
  post,
}) => {
  const updateSubmission = useUpdateContentSubmission();
  const currentStatus = post.status as ContentStatus;

  const form = useForm<EditPostFormValues>({
    resolver: zodResolver(editPostFormSchema),
    defaultValues: {
      platform: post.platform,
      type: post.type,
      status: post.status,
      post_url: post.post_url || '',
      rejection_reason: post.rejection_reason || '',
      views: post.performance_metrics?.views,
      likes: post.performance_metrics?.likes,
      comments: post.performance_metrics?.comments,
      shares: post.performance_metrics?.shares,
      saves: post.performance_metrics?.saves,
    },
  });

  const onSubmit = (values: EditPostFormValues) => {
    updateSubmission.mutate(
      {
        id: post.id,
        currentStatus,
        platform: values.platform as ContentPlatform,
        type: values.type as ContentType,
        status: values.status as ContentStatus,
        post_url: values.post_url || undefined,
        rejection_reason: values.status === 'rejected' ? values.rejection_reason : undefined,
        performance_metrics: {
          views: values.views,
          likes: values.likes,
          comments: values.comments,
          shares: values.shares,
          saves: values.saves,
          extraction_source: 'manual',
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Post</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="platform"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Platform</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="twitter">Twitter</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="reel">Reel</SelectItem>
                        <SelectItem value="story">Story</SelectItem>
                        <SelectItem value="post">Post</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="carousel">Carousel</SelectItem>
                        <SelectItem value="igtv">IGTV</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="post_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Post URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border-t pt-4">
              <div className="text-sm font-medium mb-3">Performance Metrics</div>
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="views"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Views</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="likes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Likes</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Comments</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shares"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Shares</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="saves"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Saves</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateSubmission.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {updateSubmission.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
